import { debounce } from '@/shared/utils/debounce';
import type {
  SearchHintsItem,
  SearchHintsResponse,
} from '@/shared/model/types/searchHints';
import type {
  UseSearchHintsOptions,
  UseSearchHintsReturn,
} from '@/shared/model/types/useSearchHints';
import { logError, normalizeError } from '@/shared/lib/errors';

const useSearchHints = (options: UseSearchHintsOptions): UseSearchHintsReturn => {
  const { labels, minLength = 0, initialValue = '', debounceMs = 500 } = options;
  const { $searchService } = useNuxtApp();

  const searchValue: Ref<string> = ref(initialValue);
  const itemsList: Ref<SearchHintsItem[]> = ref([]);
  const isShowHint: Ref<boolean> = ref(false);

  const modifyItems = (data: SearchHintsResponse): void => {
    itemsList.value = Object.entries(labels).reduce<SearchHintsItem[]>(
      (acc, [key, label]) => {
        const items = data[key];
        if (items && items.length && label) {
          acc.push({ id: acc.length, label, items });
        }
        return acc;
      },
      [],
    );
  };

  const debouncedSearch = debounce(async (): Promise<void> => {
    if (searchValue.value.length >= minLength && searchValue.value !== '') {
      try {
        const { data } = await $searchService.getSearchItems<SearchHintsResponse>({
          q: searchValue.value,
        });
        modifyItems(data);
        isShowHint.value = true;
      } catch (error) {
        logError(normalizeError(error), { action: 'search-hints' });
        isShowHint.value = false;
      }
    } else {
      isShowHint.value = false;
    }
  }, debounceMs);

  const searchHandler = (): void => {
    debouncedSearch();
  };

  const showHints = (): void => {
    isShowHint.value = Boolean(searchValue.value.trim());
  };

  const onHide = (): void => {
    isShowHint.value = false;
  };

  return {
    searchValue,
    itemsList,
    isShowHint,
    searchHandler,
    showHints,
    onHide,
  };
};

export default useSearchHints;
